<template>
  <form class="contact-form" @submit.prevent="handleSubmit">
    <div class="form-title">{{ $t("contact.formTitle") }}</div>
    <div class="field-row">
      <div class="field">
        <label for="name">{{ $t("contact.form.name") }}</label>
        <input
          id="name"
          v-model="formData.name"
          type="text"
          required
          :placeholder="$t('contact.form.namePlaceholder')"
          data-cursor="text"
        />
      </div>
      <div class="field">
        <label for="email">{{ $t("contact.form.email") }}</label>
        <input
          id="email"
          v-model="formData.email"
          type="email"
          required
          :placeholder="$t('contact.form.emailPlaceholder')"
          data-cursor="text"
        />
      </div>
    </div>
    <div class="field">
      <label for="message">{{ $t("contact.form.message") }}</label>
      <textarea
        id="message"
        v-model="formData.message"
        rows="4"
        required
        :placeholder="$t('contact.form.messagePlaceholder')"
        data-cursor="text"
      ></textarea>
    </div>
    <button
      type="submit"
      class="submit"
      data-cursor="hover"
      :disabled="isSubmitting"
    >
      <span v-if="!isSubmitting">{{ $t("contact.form.submit") }}</span>
      <span v-else>{{ $t("contact.form.sending") }}</span>
      <svg
        v-if="!isSubmitting"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        width="16"
        height="16"
      >
        <path d="m22 2-7 20-4-9-9-4 20-7Z" />
      </svg>
    </button>
    <div
      v-if="submitMessage"
      class="submit-message"
      :class="submitStatus"
    >
      {{ submitMessage }}
    </div>
  </form>
</template>

<script>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import emailjs from "@emailjs/browser";

export default {
  name: "ContactForm",
  setup() {
    const { locale } = useI18n();
    const formData = ref({ name: "", email: "", message: "" });
    const isSubmitting = ref(false);
    const submitMessage = ref("");
    const submitStatus = ref("");

    const handleSubmit = async () => {
      isSubmitting.value = true;
      submitMessage.value = "";
      submitStatus.value = "";
      try {
        const serviceId = process.env.VUE_APP_EMAILJS_SERVICE_ID;
        const templateId = process.env.VUE_APP_EMAILJS_TEMPLATE_ID;
        const publicKey = process.env.VUE_APP_EMAILJS_PUBLIC_KEY;
        if (!serviceId || !templateId || !publicKey) {
          throw new Error("EmailJS not configured.");
        }
        const templateParams = {
          from_name: formData.value.name,
          from_email: formData.value.email,
          name: formData.value.name,
          email: formData.value.email,
          message: formData.value.message,
          to_name: "Björn Koemans",
          site_name: "Portfolio — bjornkoemans.nl",
        };
        await emailjs.send(serviceId, templateId, templateParams, publicKey);
        submitMessage.value =
          locale.value === "nl"
            ? "Bedankt voor je bericht! Ik neem zo snel mogelijk contact met je op."
            : "Thanks for your message! I'll get back to you as soon as possible.";
        submitStatus.value = "success";
        formData.value = { name: "", email: "", message: "" };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Email send error:", error);
        submitMessage.value =
          locale.value === "nl"
            ? "Er is iets misgegaan. Probeer het opnieuw of stuur een email naar hello@bjornkoemans.nl"
            : "Something went wrong. Please try again or email me at hello@bjornkoemans.nl";
        submitStatus.value = "error";
      } finally {
        isSubmitting.value = false;
        setTimeout(() => {
          submitMessage.value = "";
          submitStatus.value = "";
        }, 5000);
      }
    };

    return {
      formData,
      isSubmitting,
      submitMessage,
      submitStatus,
      handleSubmit,
    };
  },
};
</script>

<style scoped>
.contact-form {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 28px;
  padding: 32px;
  /* No backdrop-filter here: the panel sits on a flat night ground, so the
     blur had nothing to blur and only cost a compositing layer. Glass is
     reserved for the three named singletons (nav, readout strip, mobile bar). */
  color: #fff;
}
.form-title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.65;
  margin-bottom: 22px;
}
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
@media (max-width: 600px) {
  .field-row {
    grid-template-columns: 1fr;
  }
}
.field {
  margin-bottom: 14px;
}
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
  opacity: 0.8;
}
.field input,
.field textarea {
  width: 100%;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  font-family: inherit;
  font-size: 15px;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;
}
.field input::placeholder,
.field textarea::placeholder {
  color: rgba(255, 255, 255, 0.45);
}
.field input:focus,
.field textarea:focus {
  outline: none;
  border-color: var(--accent-light);
  background: rgba(255, 255, 255, 0.14);
}
.field textarea {
  resize: vertical;
  min-height: 100px;
}
.submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 54px;
  border-radius: 14px;
  background: #fff;
  color: var(--color-primary);
  font-weight: 700;
  font-size: 15px;
  margin-top: 6px;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
}
.submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(255, 255, 255, 0.18);
}
.submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.submit-message {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(79, 179, 137, 0.18);
  border: 1px solid rgba(79, 179, 137, 0.35);
  font-size: 13px;
}
.submit-message.error {
  background: rgba(239, 68, 68, 0.18);
  border-color: rgba(239, 68, 68, 0.35);
}
</style>
